import ky, { HTTPError } from 'ky'
import type { IAPIPrincipal, IAPIPrompt, IAPIPromptVariant, IAPIDeployment, IAPIExecution, IAPIDataset, IAPIDatasetItem, IAPIDatasetItemRun, IAPIScenario, IAPIFolder, IAPIExecutionDailyStat, IAPISimulationSuite, IAPISimulationItem, IAPISimulationItemRun, TPromptType } from 'extra/types/ApiModels'

const TOKEN_KEY = 'pantheon_jwt_token'

function getToken (): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export const apiClient = ky.create({
  timeout: 600000,
  prefixUrl: '/api/v1',
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getToken()
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`)
        }
      },
    ],
  },
})

export const authClient = ky.create({
  prefixUrl: '/api/v1/auth',
})

export { HTTPError }

// Response types
export interface ApiResponse<T> {
  data: T
}

export interface ErrorResponse {
  error: string
}

// Auth types
export interface LoginResponse {
  token: string
  user: IAPIPrincipal
}

// API methods
export const api = {
  // Auth
  auth: {
    login: async (email: string, password: string): Promise<LoginResponse> => {
      const response = await authClient.post('login', {
        json: { email, password },
      }).json<ApiResponse<LoginResponse>>()
      return response.data
    },

    me: async (): Promise<IAPIPrincipal> => {
      const response = await apiClient.get('auth/me').json<ApiResponse<IAPIPrincipal>>()
      return response.data
    },
  },

  principals: {
    list: async (): Promise<Array<IAPIPrincipal>> => {
      const response = await apiClient.get('principals').json<ApiResponse<Array<IAPIPrincipal>>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`principals/${id}`)
    },

    updatePermissions: async (id: string, permissions: Array<string>): Promise<IAPIPrincipal> => {
      const response = await apiClient.put(`principals/${id}/permissions`, {
        json: { permissions },
      }).json<ApiResponse<IAPIPrincipal>>()
      return response.data
    },
  },

  // Folders
  folders: {
    list: async (): Promise<Array<IAPIFolder>> => {
      const response = await apiClient.get('folders/list').json<ApiResponse<Array<IAPIFolder>>>()
      return response.data
    },

    create: async (data: { name: string }): Promise<IAPIFolder> => {
      const response = await apiClient.post('folders', {
        json: data,
      }).json<ApiResponse<IAPIFolder>>()
      return response.data
    },

    update: async (id: string, data: { name: string }): Promise<IAPIFolder> => {
      const response = await apiClient.put(`folders/${id}`, {
        json: data,
      }).json<ApiResponse<IAPIFolder>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`folders/${id}`)
    },
  },

  // Prompts
  prompts: {
    list: async (): Promise<Array<IAPIPrompt>> => {
      const response = await apiClient.get('prompts/list').json<ApiResponse<Array<IAPIPrompt>>>()
      return response.data
    },

    get: async (id: string): Promise<IAPIPrompt> => {
      const response = await apiClient.get(`prompts/${id}`).json<ApiResponse<IAPIPrompt>>()
      return response.data
    },

    create: async (data: { name: string; type?: TPromptType; description?: string; folderId?: string | null }): Promise<IAPIPrompt> => {
      const response = await apiClient.post('prompts', {
        json: data,
      }).json<ApiResponse<IAPIPrompt>>()
      return response.data
    },

    update: async (id: string, data: { name?: string; description?: string | null; folderId?: string | null }): Promise<IAPIPrompt> => {
      const response = await apiClient.put(`prompts/${id}`, {
        json: data,
      }).json<ApiResponse<IAPIPrompt>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`prompts/${id}`)
    },
  },

  // Prompt Variants
  promptVariants: {
    listAll: async (): Promise<Array<IAPIPromptVariant>> => {
      const response = await apiClient.get('prompt-variants/list').json<ApiResponse<Array<IAPIPromptVariant>>>()
      return response.data
    },

    list: async (promptId: string): Promise<Array<IAPIPromptVariant>> => {
      const response = await apiClient.get(`prompt-variants/listPromptId/${promptId}`).json<ApiResponse<Array<IAPIPromptVariant>>>()
      return response.data
    },

    get: async (id: string): Promise<IAPIPromptVariant> => {
      const response = await apiClient.get(`prompt-variants/${id}`).json<ApiResponse<IAPIPromptVariant>>()
      return response.data
    },

    create: async (data: {
      promptId: string
      name: string
      description?: string
      config?: Record<string, any>
      variablesNames?: Array<string>
      initialChatHistory?: Array<any>
    }): Promise<IAPIPromptVariant> => {
      const response = await apiClient.post('prompt-variants', {
        json: data,
      }).json<ApiResponse<IAPIPromptVariant>>()
      return response.data
    },

    update: async (id: string, data: {
      name?: string
      description?: string | null
      config?: Record<string, any> | null
      variablesNames?: Array<string> | null
      initialChatHistory?: Array<any> | null
    }): Promise<IAPIPromptVariant> => {
      const response = await apiClient.put(`prompt-variants/${id}`, {
        json: data,
      }).json<ApiResponse<IAPIPromptVariant>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`prompt-variants/${id}`)
    },
  },

  // Deployments
  deployments: {
    listAll: async (): Promise<Array<IAPIDeployment>> => {
      const response = await apiClient.get('deployments/list').json<ApiResponse<Array<IAPIDeployment>>>()
      return response.data
    },

    list: async (promptId: string): Promise<Array<IAPIDeployment>> => {
      const response = await apiClient.get(`deployments/listPromptId/${promptId}`).json<ApiResponse<Array<IAPIDeployment>>>()
      return response.data
    },

    get: async (id: string): Promise<IAPIDeployment> => {
      const response = await apiClient.get(`deployments/${id}`).json<ApiResponse<IAPIDeployment>>()
      return response.data
    },

    create: async (data: {
      variantId: string
      label?: string
    }): Promise<IAPIDeployment> => {
      const response = await apiClient.post('deployments', {
        json: data,
      }).json<ApiResponse<IAPIDeployment>>()
      return response.data
    },

    update: async (id: string, data: {
      variantId?: string
      label?: string | null
      cache?: { enabled: boolean, ttlSeconds?: number }
    }): Promise<IAPIDeployment> => {
      const response = await apiClient.put(`deployments/${id}`, {
        json: data,
      }).json<ApiResponse<IAPIDeployment>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`deployments/${id}`)
    },
  },

  // Datasets
  datasets: {
    list: async (promptId?: string): Promise<Array<IAPIDataset>> => {
      if (promptId) {
        const response = await apiClient.get(`datasets/listPromptId/${promptId}`).json<ApiResponse<Array<IAPIDataset>>>()
        return response.data
      }
      const response = await apiClient.get('datasets/list').json<ApiResponse<Array<IAPIDataset>>>()
      return response.data
    },

    get: async (id: string): Promise<IAPIDataset> => {
      const response = await apiClient.get(`datasets/${id}`).json<ApiResponse<IAPIDataset>>()
      return response.data
    },

    create: async (data: { name: string, promptId: string, promptVariantIds: Array<string> }): Promise<IAPIDataset> => {
      const response = await apiClient.post('datasets', { json: data }).json<ApiResponse<IAPIDataset>>()
      return response.data
    },

    update: async (id: string, data: {
      name?: string
      promptVariantIds?: Array<string>
      deploymentIds?: Array<string>
      evalIds?: Array<string>
      evalConfigs?: Record<string, any>
    }): Promise<IAPIDataset> => {
      const response = await apiClient.put(`datasets/${id}`, { json: data }).json<ApiResponse<IAPIDataset>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`datasets/${id}`)
    },

    listItems: async (id: string): Promise<Array<IAPIDatasetItem>> => {
      const response = await apiClient.get(`datasets/${id}/items`).json<ApiResponse<Array<IAPIDatasetItem>>>()
      return response.data
    },

    updateItem: async (id: string, data: { evalConfigs?: Record<string, any> | null }): Promise<IAPIDatasetItem> => {
      const response = await apiClient.put(`datasets/items/${id}`, { json: data }).json<ApiResponse<IAPIDatasetItem>>()
      return response.data
    },

    executeItem: async (id: string, opts: { targetId: string, targetType: 'variant' | 'deployment' }): Promise<IAPIDatasetItemRun> => {
      const response = await apiClient.post(`datasets/items/${id}/execute`, { json: opts }).json<ApiResponse<IAPIDatasetItemRun>>()
      return response.data
    },

    evaluateRun: async (runId: string): Promise<IAPIDatasetItemRun> => {
      const response = await apiClient.post(`datasets/runs/${runId}/evaluate`).json<ApiResponse<IAPIDatasetItemRun>>()
      return response.data
    },

    cleanup: async (datasetId: string, opts?: { itemIds?: Array<string>, targetIds?: Array<string> }): Promise<void> => {
      await apiClient.post(`datasets/${datasetId}/cleanup`, { json: opts ?? {} })
    },

    getEvaluators: async (): Promise<Array<string>> => {
      const response = await apiClient.get('datasets/evaluators').json<ApiResponse<Array<string>>>()
      return response.data
    },

    copyExecutions: async (id: string, executionIds: Array<string>): Promise<{ copied: number }> => {
      const response = await apiClient.post(`datasets/${id}/copyExecutions`, { json: { executionIds } }).json<ApiResponse<{
        copied: number
      }>>()
      return response.data
    },

    duplicateItem: async (id: string): Promise<IAPIDatasetItem> => {
      const response = await apiClient.post(`datasets/items/${id}/duplicate`).json<ApiResponse<IAPIDatasetItem>>()
      return response.data
    },

    deleteItems: async (ids: Array<string>): Promise<void> => {
      await apiClient.post('datasets/items/delete', { json: { ids } })
    },
  },
  scenarios: {
    list: async (promptId: string): Promise<Array<IAPIScenario>> => {
      if (promptId) {
        const response = await apiClient.get(`scenarios/listPromptId/${promptId}`).json<ApiResponse<Array<IAPIScenario>>>()
        return response.data
      }
      const response = await apiClient.get('scenarios/list').json<ApiResponse<Array<IAPIScenario>>>()
      return response.data
    },
    get: async (id: string): Promise<IAPIScenario> => {
      const response = await apiClient.get(`scenarios/${id}`).json<ApiResponse<IAPIScenario>>()
      return response.data
    },
    create: async (data: {
      promptId: string,
      title?: string,
      chatHistory?: any,
      variablesValues?: any
    }): Promise<IAPIScenario> => {
      const response = await apiClient.post('scenarios', { json: data }).json<ApiResponse<IAPIScenario>>()
      return response.data
    },
    update: async (id: string, data: {
      title?: string,
      chatHistory?: any,
      variablesValues?: any
    }): Promise<IAPIScenario> => {
      const response = await apiClient.put(`scenarios/${id}`, { json: data }).json<ApiResponse<IAPIScenario>>()
      return response.data
    },
    delete: async (id: string): Promise<void> => await apiClient.delete(`scenarios/${id}`).json<void>(),
    execute: async (id: string, promptVariantId: string): Promise<IAPIScenario> => {
      const response = await apiClient.post(`scenarios/${id}/execute`, { json: { promptVariantId } }).json<ApiResponse<IAPIScenario>>()
      return response.data
    },
    copyFromExecutions: async (executionIds: Array<string>): Promise<{ copied: number }> => {
      const response = await apiClient.post('scenarios/copy-executions', { json: { executionIds } }).json<ApiResponse<{
        copied: number
      }>>()
      return response.data
    },
  },

  // Simulations
  simulations: {
    list: async (promptId?: string): Promise<Array<IAPISimulationSuite>> => {
      if (promptId) {
        const response = await apiClient.get(`simulations/listPromptId/${promptId}`).json<ApiResponse<Array<IAPISimulationSuite>>>()
        return response.data
      }
      const response = await apiClient.get('simulations/list').json<ApiResponse<Array<IAPISimulationSuite>>>()
      return response.data
    },

    get: async (id: string): Promise<IAPISimulationSuite> => {
      const response = await apiClient.get(`simulations/${id}`).json<ApiResponse<IAPISimulationSuite>>()
      return response.data
    },

    create: async (data: { name: string, promptId: string, promptVariantIds?: Array<string>, deploymentIds?: Array<string> }): Promise<IAPISimulationSuite> => {
      const response = await apiClient.post('simulations', { json: data }).json<ApiResponse<IAPISimulationSuite>>()
      return response.data
    },

    update: async (id: string, data: {
      name?: string
      promptVariantIds?: Array<string>
      deploymentIds?: Array<string>
      driverConfig?: Record<string, any>
      runsPerItem?: number
      maxTurns?: number
    }): Promise<IAPISimulationSuite> => {
      const response = await apiClient.put(`simulations/${id}`, { json: data }).json<ApiResponse<IAPISimulationSuite>>()
      return response.data
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`simulations/${id}`)
    },

    duplicateItem: async (id: string): Promise<IAPISimulationItem> => {
      const response = await apiClient.post(`simulations/items/${id}/duplicate`).json<ApiResponse<IAPISimulationItem>>()
      return response.data
    },

    listItems: async (suiteId: string): Promise<Array<IAPISimulationItem>> => {
      const response = await apiClient.get(`simulations/${suiteId}/items`).json<ApiResponse<Array<IAPISimulationItem>>>()
      return response.data
    },

    createItem: async (suiteId: string, data: {
      title?: string
      driverInstructions?: string
      variablesValues?: Record<string, any>
      initialChatHistory?: any
      toolInstructions?: string
      evalCriteria?: { critical: Array<string>, niceToHave: Array<string> }
    }): Promise<IAPISimulationItem> => {
      const response = await apiClient.post(`simulations/${suiteId}/items`, { json: data }).json<ApiResponse<IAPISimulationItem>>()
      return response.data
    },

    getItem: async (id: string): Promise<IAPISimulationItem> => {
      const response = await apiClient.get(`simulations/items/${id}`).json<ApiResponse<IAPISimulationItem>>()
      return response.data
    },

    updateItem: async (id: string, data: {
      title?: string
      driverInstructions?: string
      variablesValues?: Record<string, any>
      initialChatHistory?: any
      toolInstructions?: string
      evalCriteria?: { critical: Array<string>, niceToHave: Array<string> }
    }): Promise<IAPISimulationItem> => {
      const response = await apiClient.put(`simulations/items/${id}`, { json: data }).json<ApiResponse<IAPISimulationItem>>()
      return response.data
    },

    deleteItems: async (ids: Array<string>): Promise<void> => {
      await apiClient.post('simulations/items/delete', { json: { ids } })
    },

    executeItem: async (id: string, opts: { targetId: string, targetType: 'variant' | 'deployment', runIndex: number }): Promise<IAPISimulationItemRun> => {
      const response = await apiClient.post(`simulations/items/${id}/execute`, { json: opts }).json<ApiResponse<IAPISimulationItemRun>>()
      return response.data
    },

    evaluateRun: async (runId: string): Promise<IAPISimulationItemRun> => {
      const response = await apiClient.post(`simulations/runs/${runId}/evaluate`).json<ApiResponse<IAPISimulationItemRun>>()
      return response.data
    },

    cleanup: async (suiteId: string, opts?: { itemIds?: Array<string>, targetIds?: Array<string> }): Promise<void> => {
      await apiClient.post(`simulations/${suiteId}/cleanup`, { json: opts ?? {} })
    },
  },

  // Executions
  executions: {
    list: async (filters: {
      deploymentId?: string,
      searchOutput?: string,
      searchVariables?: string,
      searchChatHistory?: string,
      searchMetadata?: string,
    }, cursor?: string, limit: number = 50): Promise<{ rows: Array<IAPIExecution>, nextCursor: string | null }> => {
      const searchParams = new URLSearchParams()
      if (filters.deploymentId) searchParams.set('deploymentId', filters.deploymentId)
      if (filters.searchOutput) searchParams.set('searchOutput', filters.searchOutput)
      if (filters.searchVariables) searchParams.set('searchVariables', filters.searchVariables)
      if (filters.searchChatHistory) searchParams.set('searchChatHistory', filters.searchChatHistory)
      if (filters.searchMetadata) searchParams.set('searchMetadata', filters.searchMetadata)
      if (cursor) searchParams.set('cursor', cursor)
      searchParams.set('limit', limit.toString())

      const response = await apiClient.get('executions/list', { searchParams }).json<ApiResponse<Array<IAPIExecution>> & {
        meta: { nextCursor: string | null }
      }>()
      return { rows: response.data, nextCursor: response.meta.nextCursor }
    },

    get: async (id: string): Promise<IAPIExecution> => {
      const response = await apiClient.get(`executions/${id}`).json<ApiResponse<IAPIExecution>>()
      return response.data
    },

    listDeployments: async (): Promise<Array<{ id: string, label: string | null, promptId: string }>> => {
      const response = await apiClient.get('executions/deployments').json<ApiResponse<Array<{
        id: string,
        label: string | null,
        promptId: string
      }>>>()
      return response.data
    },

    delete: async (ids: Array<string>): Promise<void> => {
      await apiClient.post('executions/delete', { json: { ids } })
    },

    stats: async (): Promise<Array<IAPIExecutionDailyStat>> => {
      const response = await apiClient.get('executions/stats').json<ApiResponse<Array<IAPIExecutionDailyStat>>>()
      return response.data
    },
  },
}
